import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {  FaEdit, FaKey  } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { showToast } from "../../../helpers/ToastifyHelper";
import SessionStorageService from "../../../services/StorageService";
import IlIlceListeAPI from "../../../apis/OB/Get/IlIlceListeAPI";
import { sortDataByField } from "../../../helpers/MoneyFormat";
import MahalleListeAPI from "../../../apis/OB/Get/MahalleListeAPI";
import CaddeSokakAPI from "../../../apis/OB/Get/CaddeSokakAPI";
import AuthAPI, { UyelikGuncelleRequest } from "../../../apis/AuthAPI";
import { Profile } from "../profil/Profil";
import KullaniciYonetimiAPI, { KullaniciSearchRequest } from "../../../apis/KullaniciYonetimi";
import KullaniciSifreDegistir, { KullaniciSifreDegistirRequest } from '../../../apis/OB/SaveApi/KullaniciSifreDegistir';
import { debounce } from 'lodash';
import CloseIcon from "@mui/icons-material/Close";

interface UserData {
  tcNo: string;
  adi: string;
  soyadi: string;
  dogumTarihi: string;
  cepTelefon: string;
  ePosta: string;
  adres: string;
  kullaniciId: number;
  aktifMi: string;
}

export default function UyelikUpdate() {
  const [tckn, setTckn] = useState("");
  const [adi, setAdi] = useState("");
  const [soyadi, setSoyadi] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedData, setSelectedData] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [addressData, setAddressData] = useState<any>(null);
  const [validationErrors, setValidationErrors] = useState({
    adresSqId: false,
    blokNo: false,
    caddeSokakAdi: false,
    caddeSokakKodu: false,
    daireNo: false,
    ePosta: false,
    guncelleyen: false,
    kanGrubu: false,
    kapiNo: false,
    mahalleAdi: false,
    mahalleKodu: false,
    postaKodu: false,
    telefon: false,
    ilIlceKodu: false
  });

  const PAGE_SIZE = 8;
  const [currentPage, setCurrentPage] = useState(1);
  const [mahalleList, setMahalleList] = useState<any[]>([]);
  const [caddeSokakList, setCaddeSokakList] = useState<any[]>([]);
  const [ilIlceList, setIlIlceList] = useState<any[]>([]);
  const [secilenIlIlce, setSecilenIlIlce] = useState<any>("");
  const navigate = useNavigate();

  const [confirmationDialog, setConfirmationDialog] = useState<any>({
    open: false,
    user: null,
    newStatus: "",
    currentStatus: ""
  });

  const [switchStates, setSwitchStates] = useState<{ [key: number]: boolean }>({});

  const [editDialog, setEditDialog] = useState({
    open: false,
    userId: null
  });

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [newPassword, setNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const displayedResults = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return searchResults.slice(startIndex, startIndex + PAGE_SIZE);
  }, [searchResults, currentPage]);

  const user = SessionStorageService.getUserInfo();

  useEffect(() => {
    if (user == null) {
      navigate("/");
    } else {
      if (user.rol !== "ADMIN") {
        navigate("/");
      }
    }
  }, []);

  useEffect(() => {
    const fetchIlIlceler = async () => {
      try {
        const response = await IlIlceListeAPI.getIlIlceListe(undefined);
        let orderedData = response.data.length > 0 ? sortDataByField(response.data, "ilIlceAdi") : [];
        setIlIlceList(orderedData || []);
      } catch (error) {
        console.error("İl/İlçe listeleme hatası:", error);
      }
    };
    fetchIlIlceler();
  }, []);

  useEffect(() => {
    const fetchMahalleler = async () => {
      try {
        const response = await MahalleListeAPI.getMahalleListe(undefined,undefined);
        let orderedData = response.data.length > 0 ? sortDataByField(response.data, "mahalleAdi") : [];
        setMahalleList(orderedData || []);
      } catch (error) {
        console.error("Mahalle listeleme hatası:", error);
      }
    };
    fetchMahalleler();
  }, []);

  useEffect(() => {
    if (addressData?.rfOrtMahalleKoyler) {
      const fetchCaddeSokaklar = async () => {
        try {
          const response = await CaddeSokakAPI.getCaddeSokakListe(undefined,addressData.rfOrtMahalleKoyler);
          let orderedData = response.data.length > 0 ? sortDataByField(response.data, "caddeSokakAdi") : [];
          setCaddeSokakList(orderedData || []);
        } catch (error) {
          console.error("Cadde/Sokak listeleme hatası:", error);
        }
      };
      fetchCaddeSokaklar();
    } else {
      setCaddeSokakList([]);
    }
  }, [addressData?.rfOrtMahalleKoyler]);

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((tckn: string, adi: string, soyadi: string) => {
      KullaniciYonetimiAPI.kullaniciSearch(new KullaniciSearchRequest({tcNo: tckn, adi: adi, soyadi: soyadi}))
        .then((response) => {
          setSearchResults(response.data);
        })
        .catch((error) => {
          console.error(error);
          setSearchResults([]);
        });
    }, 300),
    []
  );

  // Memoized handlers
  const handleSearch = useCallback(() => {
    debouncedSearch(tckn, adi, soyadi);
  }, [tckn, adi, soyadi, debouncedSearch]);

  const handleEdit = useCallback((rowData: any) => {
    setEditDialog({
      open: true,
      userId: rowData.kullaniciId
    });
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setEditDialog({
      open: false,
      userId: null
    });
  }, []);

  const handleFieldChange = (field: string, value: string) => {
    if (!selectedData) return;
    const updated = { ...selectedData, [field]: value };
    setSelectedData(updated);
    setIsEdited(true);
  };

  const handleAddressChange = (field: string, value: string) => {
    if (!addressData) return;
    const updated = { ...addressData, [field]: value };
    setAddressData(updated);
    setSecilenIlIlce(updated.ilIlceKodu);
    setIsEdited(true);
  };

  const handleUpdate = async () => {
    if (!isEdited || !selectedData) return;

    const cleanedPhone = (selectedData.cepTelefon || "").replace(/\s/g, "");
    const request: UyelikGuncelleRequest = {
      adresSqId: addressData?.sqId,
      blokNo: addressData?.blokNo,
      caddeSokakAdi: addressData?.caddeAdi || "",
      caddeSokakKodu:
        addressData?.rfOrtCaddeSokak && addressData.rfOrtCaddeSokak !== ""
          ? addressData.rfOrtCaddeSokak
          : undefined,
      daireNo: addressData?.daireNo,
      ePosta: selectedData.ePosta || "",
      guncelleyen: user.adi || "",
      kanGrubu: selectedData.kanGrubu,
      kapiNo: addressData?.kapiNo,
      mahalleAdi: addressData?.mahalleAdi || "",
      kullaniciId: selectedData.kullaniciId,
      mahalleKodu:
        addressData?.rfOrtMahalleKoyler && addressData.rfOrtMahalleKoyler !== ""
          ? addressData.rfOrtMahalleKoyler
          : undefined,
      postaKodu: addressData?.postaKodu ? Number(addressData.postaKodu) : undefined,
      telefon: Number(cleanedPhone),
      siteApartmanAdi: addressData?.siteApartmanAdi,
      ilIlceKodu: addressData?.ilIlceKodu ? String(addressData.ilIlceKodu) : "",
      rol: undefined
    };

    const fieldErrors : any = {
      adresSqId: !request.adresSqId,
      blokNo: !request.blokNo,
      caddeSokakAdi: !request.caddeSokakAdi,
      caddeSokakKodu: !request.caddeSokakKodu,
      daireNo: !request.daireNo,
      ePosta: !request.ePosta,
      guncelleyen: !request.guncelleyen,
      kapiNo: !request.kapiNo,
      mahalleAdi: !request.mahalleAdi,
      mahalleKodu: !request.mahalleKodu,
      postaKodu: request.postaKodu === undefined,
      telefon: !cleanedPhone,
      ilIlceKodu: !request.ilIlceKodu
    };

    if (Object.values(fieldErrors).some((err) => err)) {
      setValidationErrors(fieldErrors);
      console.log("validationErrors", fieldErrors);
      showToast("Lütfen tüm alanları doldurunuz.");
      return;
    }

    try {
      const response = await AuthAPI.ProfilGuncelleme(request);
      if (response.data?.sonuc === 0 && response.success) {
        showToast("Güncelleme başarılı!");
        setIsModalOpen(false);
      } else {
        showToast("Güncelleme sırasında bir hata oluştu.");
      }
    } catch (error) {
      showToast("Güncelleme sırasında bir hata oluştu.");
      console.error("Güncelleme hatası:", error);
    }
  };

  const handleStatusChange = useCallback((user: any, newStatus: string) => {
    setConfirmationDialog({
      open: true,
      user,
      newStatus,
      currentStatus: user.aktifMi
    });
  }, []);

  const handleConfirmStatusChange = async () => {
    const originalStatus = confirmationDialog.currentStatus;
    try {
      const request: UyelikGuncelleRequest = {
        kullaniciId: confirmationDialog.user.kullaniciId,
        telefon: confirmationDialog.user.cepTelefon,
        ePosta: confirmationDialog.user.email,
        guncelleyen: user.adi || "",
        aktifMi: confirmationDialog.newStatus
      };

      const response = await AuthAPI.ProfilGuncelleme(request);
      
      if (response.data?.sonuc === 0 && response.success) {
        // Update the local state only if API call is successful
        setSearchResults(prevResults => 
          prevResults.map(user => 
            user.kullaniciId === confirmationDialog.user.kullaniciId
              ? { ...user, aktifMi: confirmationDialog.newStatus }
              : user
          )
        );
        
        showToast(`Kullanıcı durumu başarıyla ${confirmationDialog.newStatus === "E" ? 'aktif' : 'pasif'} yapıldı.`);
      } else {
        throw new Error("API call failed");
      }
    } catch (error) {
      // Revert to original state on error
      setSearchResults(prevResults => 
        prevResults.map(user => 
          user.kullaniciId === confirmationDialog.user.kullaniciId
            ? { ...user, aktifMi: originalStatus }
            : user
        )
      );
      showToast("Kullanıcı durumu güncellenirken bir hata oluştu.", "error");
    } finally {
      setConfirmationDialog({ open: false, user: null, newStatus: "", currentStatus: "" });
    }
  };

  const handleCancelStatusChange = () => {
    setConfirmationDialog({ open: false, user: null, newStatus: "", currentStatus: "" });
  };

  // Memoized password change handlers
  const handlePasswordChange = useCallback(async () => {
    if (newPassword.length < 6) {
      setPasswordError("Şifre en az 6 karakter olmalıdır");
      return;
    }

    try {
      // Kullanıcı verilerinde vergiNo alanı varsa onu kullan, yoksa tcNo kullan
      const hasVergiNo = selectedUser.vergiNo && selectedUser.vergiNo.trim() !== "";
      
      let request;
      if (hasVergiNo) {
        // Vergi No ile şifre değiştirme
        request = new KullaniciSifreDegistirRequest(
          newPassword,
          undefined, // kullaniciKodu boş
          selectedUser.vergiNo // vergiNo dolu
        );
      } else {
        // TC Kimlik No ile şifre değiştirme
        request = new KullaniciSifreDegistirRequest(
          newPassword,
          selectedUser.tcNo, // kullaniciKodu dolu
          undefined // vergiNo boş
        );
      }

      const response = await KullaniciSifreDegistir.saveKullaniciSifreDegistirSave(request);

      if (response.data.sonuc === 0) {
        showToast("Şifre başarıyla değiştirildi");
        setPasswordDialogOpen(false);
        setNewPassword("");
        setPasswordError("");
      } else {
        setPasswordError(response.data.sonucAciklama);
      }
    } catch (error) {
      setPasswordError("Şifre değiştirme sırasında bir hata oluştu");
      console.error("Şifre değiştirme hatası:", error);
    }
  }, [newPassword, selectedUser]);

  const handlePasswordInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(e.target.value);
    setPasswordError("");
  }, []);

  const handleClosePasswordDialog = useCallback(() => {
    setPasswordDialogOpen(false);
    setNewPassword("");
    setPasswordError("");
  }, []);

  // Memoized table row render
  const renderTableRow = useCallback((row: any, index: number) => (
    <TableRow key={index}>
      <TableCell>
        <FaEdit
          style={{ cursor: "pointer" }}
          className="text-warning"
          onClick={() => handleEdit(row)}
          size={24}
        />
      </TableCell>
      <TableCell>
        <FaKey 
          size={24} 
          color="gray" 
          onClick={() => {
            setSelectedUser(row);
            setPasswordDialogOpen(true);
          }} 
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={row.aktifMi === "E"}
          onClick={(e) => {
            e.preventDefault();
            handleStatusChange(row, row.aktifMi === "E" ? "H" : "E");
          }}
          color="primary"
          inputProps={{ 'aria-label': 'Aktif/Pasif durumu' }}
        />
      </TableCell>
      <TableCell>{row.tcNo}</TableCell>
      <TableCell>{row.vergiNo || "-"}</TableCell>
      <TableCell>{row.adi}</TableCell>
      <TableCell>{row.soyadi}</TableCell>
      <TableCell>{row.dogumTarihi ? row.dogumTarihi.split("T")[0] : ""}</TableCell>
      <TableCell>{row.cepTelefon}</TableCell>
      <TableCell>{row.ePosta}</TableCell>
      <TableCell>{row.adres}</TableCell>
    </TableRow>
  ), [handleEdit, handleStatusChange]);

  // Memoized table body
  const tableBody = useMemo(() => (
    <TableBody>
      {displayedResults.map((row, index) => renderTableRow(row, index))}
    </TableBody>
  ), [displayedResults, renderTableRow]);

  return (
    <Box className="d-flex flex-column w-100 py-5 px-1 px-sm-1 px-md-2 px-lg-3 m-0">
     
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="TCKimlikNo"
            value={tckn}
            onChange={(e) => setTckn(e.target.value)}
            variant="outlined"
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Adı"
            value={adi}
            onChange={(e) => setAdi(e.target.value)}
            variant="outlined"
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Soyadı"
            value={soyadi}
            onChange={(e) => setSoyadi(e.target.value)}
            variant="outlined"
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            fullWidth
            sx={{ height: "100%" }}
          >
            Ara
          </Button>
        </Grid>
      </Grid>

      {/* Results table */}
      <div style={{ overflowX: 'auto', width: '100%' }}>
        <Table className="mt-3" style={{ minWidth: '1000px' }}>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell>Aktiflik</TableCell>
              <TableCell>TC Kimlik No</TableCell>
              <TableCell>Vergi No</TableCell>
              <TableCell>Adı</TableCell>
              <TableCell>Soyadı</TableCell>
              <TableCell>Doğum Tarihi</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Adres</TableCell>
            </TableRow>
          </TableHead>
          {tableBody}
        </Table>
      </div>

      {/* Pagination */}
      <Box mt={2} display="flex" justifyContent="center">
        <Pagination
          count={Math.ceil(searchResults.length / PAGE_SIZE)}
          page={currentPage}
          onChange={(e, value) => setCurrentPage(value)}
          color="primary"
        />
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmationDialog.open}
        onClose={handleCancelStatusChange}
      >
        <DialogTitle>Kullanıcı Durumu Değiştirme</DialogTitle>
        <DialogContent>
          <Typography>
            {confirmationDialog.user?.adi} {confirmationDialog.user?.soyadi} isimli kullanıcının durumunu 
            {confirmationDialog.newStatus === "E" ? ' aktif' : ' pasif'} yapmak istediğinize emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
          color="secondary"
          onClick={handleCancelStatusChange}>
            İptal
          </Button>
          <Button onClick={handleConfirmStatusChange} color="primary" variant="contained">
            Onayla
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={handleCloseEditDialog}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            m: "0.5rem",
            maxWidth: '100%',
            width: '100%',
            maxHeight: '100vh',
          }
        }}
      >
        <DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCloseEditDialog}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme: any) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        
        </DialogTitle>
        <DialogContent>
          {editDialog.userId && (
            <Profile 
              isAdminEdit={true} 
              userId={editDialog.userId}
              onClose={handleCloseEditDialog}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Şifre Değiştirme Dialog */}
      <Dialog open={passwordDialogOpen} onClose={handleClosePasswordDialog}>
        <DialogTitle>Yeni Şifre</DialogTitle>
        <DialogContent>
          <p>
            {selectedUser?.vergiNo && selectedUser.vergiNo.trim() !== "" 
              ? `${selectedUser.vergiNo} Vergi numaralı` 
              : `${selectedUser?.tcNo} T.C Kimlik numaralı`
            } {selectedUser?.adi + " " + selectedUser?.soyadi} kullanıcısının şifresini değiştiriyorsunuz.
          </p>
          <TextField
            autoFocus
            margin="dense"
            label="Yeni Şifre"
            type="password"
            fullWidth
            value={newPassword}
            onChange={handlePasswordInputChange}
            error={!!passwordError}
            helperText={passwordError}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            color="secondary"
            onClick={handleClosePasswordDialog}
          >
            İptal
          </Button>
          <Button 
            onClick={handlePasswordChange} 
            color="primary"
          >
            Değiştir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
